/**
 * app_lock_setting_controller.js — the Settings toggle behind the device app lock.
 *
 * Deliberately a second, tiny controller rather than an optional target on
 * app_lock_controller.js: the lock itself is an app-wide singleton mounted on
 * base.html, and this is one checkbox that exists on one screen. They share the
 * preference through app_lock_controller.js's named exports, so the storage key
 * has exactly one owner.
 *
 * OPT-IN, AND OFF UNTIL PROVEN. The lock is off until the user turns it on (no
 * stored key means disabled), and turning it ON runs the biometric check once,
 * right here, before anything is persisted. That is not ceremony:
 * canEvaluatePolicy fails outright on a device with no passcode enrolled, so
 * without this the switch would happily arm a lock the user cannot get past, and
 * they would only discover it the next time they came back to the app. Verifying
 * at the moment of consent means the only devices that can arm the lock are the
 * ones that have already demonstrated they can open it.
 *
 * Turning it OFF is unconditional and takes effect immediately — never gate the
 * exit from a lock on the credential the user may have just lost.
 *
 * The preference is DEVICE-LOCAL, which is why this control sits outside the
 * General tab's <form>: there is nothing to POST and no "Save" to press. A
 * server-side setting would follow the account onto every device the tradie
 * signs in from, which is the wrong meaning for "lock THIS phone".
 *
 * Once armed, the lock engages from the next background transition, not
 * immediately — re-challenging one tap after the user asked for the feature
 * teaches them nothing and reads as a bug.
 *
 * <!-- CSS: static/css/app-lock.css (the overlay this preference arms) -->
 */
import { Controller } from "@hotwired/stimulus";
import { isLockEnabled, isLockSupported, setLockEnabled } from "./app_lock_controller.js";

export default class extends Controller {
    static targets = ["toggle", "status"];

    connect() {
        // Render actual state: the checkbox ships unchecked in the template
        // because the server cannot know a device-local preference.
        if (this.hasToggleTarget) {
            this.toggleTarget.checked = isLockEnabled();
        }
        this.setStatus("");
    }

    async change() {
        if (!this.hasToggleTarget) return;
        const wantsOn = this.toggleTarget.checked;

        if (!wantsOn) {
            setLockEnabled(false);
            this.setStatus("App Lock is off.");
            return;
        }

        const bridge = window.ChippieBridge;
        if (!isLockSupported() || !bridge || typeof bridge.biometricsLock !== "function") {
            this.refuse("App Lock needs the Chippie app on your phone.");
            return;
        }

        this.toggleTarget.disabled = true;
        try {
            const ok = await bridge.biometricsLock({ reason: "Turn on App Lock" });
            if (ok) {
                setLockEnabled(true);
                this.setStatus("App Lock is on. It starts the next time you leave the app.");
            } else {
                // Cancelled, failed, or a device that cannot evaluate the policy
                // at all. All three mean the same thing here: do not arm it.
                this.refuse("Couldn't verify it's you, so App Lock stays off. Check Face ID, Touch ID or your passcode is set up on this phone.");
            }
        } catch {
            this.refuse("Couldn't turn on App Lock. Try again.");
        } finally {
            this.toggleTarget.disabled = false;
        }
    }

    /** Leave the stored preference alone and put the switch back where it was. */
    refuse(message) {
        setLockEnabled(false);
        this.toggleTarget.checked = false;
        this.setStatus(message);
    }

    setStatus(message) {
        if (!this.hasStatusTarget) return;
        this.statusTarget.textContent = message;
        this.statusTarget.hidden = !message;
    }
}
